import { fetchWithAuth } from './fetchwithauth';

export interface AppComponentVersion {
  version: string | null;
  commit: string | null;
  available: boolean;
  startedAtUtc: string | null;
}

export interface AppVersionResponse {
  frontend: AppComponentVersion;
  webInterface: AppComponentVersion;
  core: AppComponentVersion;
  deployedAtUtc: string | null;
  compatible: boolean;
}

export interface LoadedBuildVersion {
  version: string;
  commit: string | null;
  identity: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function parseComponentVersion(value: unknown): AppComponentVersion | null {
  if (!isRecord(value)
    || typeof value.available !== 'boolean'
    || !isNullableString(value.version)
    || !isNullableString(value.commit)
    || !isNullableString(value.startedAtUtc)) {
    return null;
  }

  return {
    version: value.version,
    commit: value.commit,
    available: value.available,
    startedAtUtc: value.startedAtUtc
  };
}

function parseAppVersionResponse(value: unknown): AppVersionResponse | null {
  if (!isRecord(value)
    || typeof value.compatible !== 'boolean'
    || !isNullableString(value.deployedAtUtc)) {
    return null;
  }

  const frontend = parseComponentVersion(value.frontend);
  const webInterface = parseComponentVersion(value.webInterface);
  const core = parseComponentVersion(value.core);
  if (!frontend || !webInterface || !core) {
    return null;
  }

  return {
    frontend,
    webInterface,
    core,
    deployedAtUtc: value.deployedAtUtc,
    compatible: value.compatible
  };
}

export function parseLoadedBuildVersion(identity: string): LoadedBuildVersion {
  const normalized = identity.trim() || 'unknown';
  const match = /^(\d+\.\d+\.\d+)\+([0-9a-f]{8})$/i.exec(normalized);

  return match
    ? { version: match[1], commit: match[2].toLowerCase(), identity: normalized }
    : { version: normalized, commit: null, identity: normalized };
}

export async function fetchAppVersion(): Promise<AppVersionResponse> {
  const response = await fetchWithAuth('/api/app/version', {
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error('Kunne ikke hente versjonsinformasjon.');
  }

  const parsed = parseAppVersionResponse(await response.json());
  if (!parsed) {
    throw new Error('Versjonsresponsen har ugyldig format.');
  }

  return parsed;
}
